import { DataTable, type Column, fmt, FilterBlock } from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { fromTimestamp, normalizeForSearch } from "@racoon/lib";
import { useMemo, useState } from "react";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { useListCounterpartiesQuery } from "~/shared/api/organization/organization.grpc";
import { useTableSettings } from "~/shared/hooks/useTableSettings";

export default function ServiceProvidersPage() {
  const t = useT();
  const { selectedCompanyId } = useSelectedCompany();
  const { columnConfig, handleColumnConfigChange } = useTableSettings(
    "service_providers",
    selectedCompanyId || undefined,
  );
  const [searchValue, setSearchValue] = useState("");

  const { data, isLoading } = useListCounterpartiesQuery(
    { organizationId: BigInt(selectedCompanyId ?? 0) },
    { skip: !selectedCompanyId },
  );

  const rows = useMemo<ConnectProto.Counterparty[]>(() => {
    const list = data?.counterparties ?? [];
    const term = normalizeForSearch(searchValue);
    if (!term) return list;
    return list.filter((c) => normalizeForSearch(c.organization?.name).includes(term));
  }, [data, searchValue]);

  const columns = useMemo<Column<ConnectProto.Counterparty>[]>(() => {
    return [
      {
        id: "index",
        header: "#",
        accessor: (_r, i) => i + 1,
        width: 64,
      },
      {
        id: "name",
        header: "Название",
        accessor: (c) => c.organization?.name ?? "",
        sortable: true,
        width: 300,
      },
      {
        id: "status",
        header: "Статус",
        width: 160,
        sortable: true,
        accessor: (c) => c.contractStatus === ConnectProto.ContractStatus.ACTIVE,
        cell: (info) =>
          info.getValue() ? (
            <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 text-xs px-2 py-1">
              Активен
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-slate-200 text-slate-700 text-xs px-2 py-1">
              Неактивен
            </span>
          ),
      },
      {
        id: "created_at",
        header: "Дата создания",
        accessor: (c) => fromTimestamp(c.organization?.createdAt),
        sortable: true,
        sortFn: (a, b) =>
          (fromTimestamp(a.organization?.createdAt)?.getTime() ?? 0) -
          (fromTimestamp(b.organization?.createdAt)?.getTime() ?? 0),
        width: 220,
        cell: (info) => {
          const d = info.getValue() as Date | null;
          return d ? fmt.dateTime(d) : "-";
        },
      },
    ];
  }, []);

  if (!selectedCompanyId) {
    return <div>{t(Namespace.Organizations, "selectOrganization")}</div>;
  }

  return (
    <div className="min-h-screen w-full flex flex-col space-y-4">
      <FilterBlock
        onSearchChange={setSearchValue}
        searchValue={searchValue}
        totalCount={rows.length}
        itemType="serviceProviders"
        direction="row"
      />

      <DataTable<ConnectProto.Counterparty>
        columns={columns}
        data={rows}
        loading={isLoading}
        initialSort={{ id: "name", dir: "asc" }}
        rowKey={(c, i) => (c.organization ? String(c.organization.id) : `row-${i}`)}
        selectable={false}
        caption={null}
        zebra
        className="min-w-[100%]"
        columnConfig={columnConfig}
        onColumnConfigChange={handleColumnConfigChange}
        namespace={Namespace.Table}
      />
    </div>
  );
}
